import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useAutocompleteServiceLegacy } from '#src/app/shared/hooks/googlePlaces';
import { useLocale } from '#src/app/shared/hooks/l10n';

import type { GeoScope, Place } from '../../shared/types';
import { getPlacePredictions } from '../api/getPlacePredictions';

// TODO make this a bit more generic so it can be used in different ways (like by admin/decisionMakerEdit)
export function usePlacePredictions({
	input,
	geoScope,
	inputLengthApiCallThreshold,
}: {
	input: string;
	geoScope?: GeoScope;
	inputLengthApiCallThreshold?: number;
}): UsePlacePredictionsResult {
	const locale = useLocale();
	const autocompleteService = useAutocompleteServiceLegacy();

	return useQuery({
		// `autocompleteService` is a non-serializable Google Maps object: it stays out of the key and is closed over
		// instead. `isReady` makes the key change once the service loads so the query re-runs with it.
		queryKey: [
			'placePredictions',
			{ input, locale, geoScope, inputLengthApiCallThreshold, isReady: !!autocompleteService },
		],
		queryFn: async () =>
			getPlacePredictions({ input, locale, geoScope }, autocompleteService, inputLengthApiCallThreshold),
		// keep showing the previous predictions while the next input's results load
		placeholderData: keepPreviousData,
		// getPlacePredictions resolves locally (without a request) for an empty or too-short input; the default 'online'
		// mode would pause the query while offline and leave the previous predictions selectable
		networkMode: 'always',
	});
}

export type UsePlacePredictionsResult = UseQueryResult<Place[]>;
